import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { NumberFormatNorwegianCurrency, formatCurrencyValue } from '@eika/currency-input';

import type { Currency } from '../../../generated-types';

import './AmountInput.scss';

type NumberFormatValues = import('react-number-format').NumberFormatValues;

interface Props {
    value?: number;
    ariaLabel?: string;
    onChange: (value?: number) => void;
    locked?: boolean;
    focusFirst?: boolean;
    currency?: Currency;
}

const AmountInput: React.FC<Props> = ({
    value,
    ariaLabel,
    currency = 'NOK',
    onChange,
    locked = false,
    focusFirst = false,
}) => {
    const [inputValue, setInputValue] = useState<string | undefined>();
    const [hasFocus, setHasFocus] = useState(false);

    const width = useMemo(() => {
        return Math.max(inputValue?.length ?? 1, 1);
    }, [inputValue]);

    useEffect(() => {
        setInputValue(value ? formatCurrencyValue(value) : undefined);
    }, [value]);

    const filteredCurrency = currency === 'NOK' ? 'kr' : currency;

    const handleValueChange = useCallback(
        (values: NumberFormatValues) => {
            onChange(values.floatValue);
            setInputValue(values.formattedValue);
        },
        [onChange],
    );
    const additionalProps = focusFirst
        ? {
              'data-focus-first': 'true',
          }
        : {};

    return (
        <div
            className="amount-input"
            style={
                {
                    '--input-max-width': `${width}ex`,
                } as React.CSSProperties
            }
        >
            <label className="amount-input__label">
                {/*  eslint-disable react/jsx-props-no-spreading */}
                <NumberFormatNorwegianCurrency
                    name="amount"
                    value={inputValue ?? ''}
                    onValueChange={handleValueChange}
                    onFocus={() => setHasFocus(true)}
                    onBlur={() => setHasFocus(false)}
                    suffix=""
                    placeholder={hasFocus ? ' ' : '0'}
                    maxLength={12}
                    autoComplete="off"
                    className={classNames('amount-input__amount', { 'amount-input__locked': locked })}
                    aria-label={ariaLabel}
                    disabled={locked}
                    data-testid="amount-field"
                    {...additionalProps}
                />
                <span
                    className={classNames('amount-input__currency-type', {
                        'amount-input__currency-type-locked': locked,
                    })}
                >
                    {String(filteredCurrency)}
                </span>
            </label>
        </div>
    );
};

export default AmountInput;
